import { Injectable } from '@angular/core';
import { Response } from '@angular/http';
import { Observable } from 'rxjs/Rx';

import { AzureApiService } from '../../services/azure-api.service';
import { DtoCustomerOrigin } from './models/DtoCustomerOrigin';
import { DtoBtwGroup } from './models/DtoBtwGroup';
import { DtoMutationGroup } from './models/DtoMutationGroup';
import { DtoTreatmentSalesgroup } from './models/DtoTreatmentSalesgroup';
import { DtoSaveResult } from '../../shared/models/DtoSaveResult';
import { DtoDeleteResult } from '../../shared/models/DtoDeleteResult';
import { DtoArticleSalesgroup } from "./models/DtoArticleSalesgroup";
import { DtoCashregGroup } from "./models/DtoCashregGroup";
import { DtoSupplier } from "./models/DtoSupplier";
import { DtoDepartment } from "./models/DtoDepartment";
import { DtoCheckResult } from "../../shared/models/DtoCheckResult";
import {DtoPlannerSettingsUser} from "../settings-planner/models/DtoPlannerSettingsUser";
import {DtoFinancialSettingsUser} from "./models/DtoFinancialSettingsUser";
import {DtoOtherSettingsUserSetting} from "./models/DtoOtherSettingsUserSetting";
import {DtoPasswordChange} from "./models/DtoPasswordChange";

@Injectable()
export class SettingsBaseService {
    private apiUrlCustomerOrigin: string;
    private apiUrlBtwGroup: string;
    private apiUrlMutationGroup: string;
    private apiUrlTreatmentSalesgroup: string;
    private apiUrlArticleSalesgroup: string;
    private apiUrlCashregGroup: string;
    private apiUrlDepartment: string;
    private apiUrlSupplier: string;
    private apiUrlUser: string;

    constructor(private http: AzureApiService) {
        this.apiUrlCustomerOrigin = 'customerorigin';
        this.apiUrlBtwGroup = 'btwgroup';
        this.apiUrlMutationGroup = 'mutationgroup';
        this.apiUrlTreatmentSalesgroup = 'treatmentsalesgroup';
        this.apiUrlArticleSalesgroup = 'articlesalesgroup';
        this.apiUrlCashregGroup = 'cashreggroup';
        this.apiUrlDepartment = 'department';
        this.apiUrlSupplier = 'supplier';
        this.apiUrlUser = 'user';
    }

    // TREATMENT SALESGROUP
    public getTreatmentSalesgroups = (): Observable<DtoTreatmentSalesgroup[]> => {
        return this.http.get<DtoTreatmentSalesgroup[]>(`${this.apiUrlTreatmentSalesgroup}`);
    };

    public getTreatmentSalesgroupById = (id: number): Observable<DtoTreatmentSalesgroup> => {
        return this.http.get<DtoTreatmentSalesgroup>(`${this.apiUrlTreatmentSalesgroup}/${id}`);
    };

    public saveTreatmentSalesgroup = (dtoObject: DtoTreatmentSalesgroup): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(this.apiUrlTreatmentSalesgroup, dtoObject);
    };

    public deleteTreatmentSalesgroupById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlTreatmentSalesgroup}/${id}`);
    };

    public getTreatmentSalesgroupNextIndex = (): Observable<number> => {
        return this.http.get<number>(`${this.apiUrlTreatmentSalesgroup}/nextindex`);
    };

    public checkTreatmentSalesgroupIndexAvailable = (id: number, index: number): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlTreatmentSalesgroup}/indexavailable/${id}/${index}`);
    };

    // ARTICLE SALESGROUP
    public getArticleSalesgroups = (): Observable<DtoArticleSalesgroup[]> => {
        return this.http.get<DtoArticleSalesgroup[]>(`${this.apiUrlArticleSalesgroup}`);
    };

    public getArticleSalesgroupById = (id: number): Observable<DtoArticleSalesgroup> => {
        return this.http.get<DtoArticleSalesgroup>(`${this.apiUrlArticleSalesgroup}/${id}`);
    };

    public saveArticleSalesgroup = (dtoObject: DtoArticleSalesgroup): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(this.apiUrlArticleSalesgroup, dtoObject);
    };

    public deleteArticleSalesgroupById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlArticleSalesgroup}/${id}`);
    };

    public getArticleSalesgroupNextIndex = (): Observable<number> => {
        return this.http.get<number>(`${this.apiUrlArticleSalesgroup}/nextindex`);
    };

    public checkArticleSalesgroupIndexAvailable = (id: number, index: number): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlArticleSalesgroup}/indexavailable/${id}/${index}`);
    };

    // CASHREGGROUP
    public getCashregGroups = (): Observable<DtoCashregGroup[]> => {
        return this.http.get<DtoCashregGroup[]>(`${this.apiUrlCashregGroup}`);
    };

    public getCashregGroupById = (id: number): Observable<DtoCashregGroup> => {
        return this.http.get<DtoCashregGroup>(`${this.apiUrlCashregGroup}/${id}`);
    };

    public saveCashregGroup = (dtoObject: DtoCashregGroup): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(this.apiUrlCashregGroup, dtoObject);
    };

    public deleteCashregGroupById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlCashregGroup}/${id}`);
    };

    public getCashregGroupNextIndex = (): Observable<number> => {
        return this.http.get<number>(`${this.apiUrlCashregGroup}/nextindex`);
    };

    public checkCashregGroupIndexAvailable = (id: number, index: number): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlCashregGroup}/indexavailable/${id}/${index}`);
    };

    public checkDeleteSubgroup = (id: number): Observable<DtoDeleteResult> => {
        return this.http.get<DtoDeleteResult>(`${this.apiUrlCashregGroup}/subgroup/checkdelete/${id}`);
    };

    // DEPARTMENT
    public getDepartments = (): Observable<DtoDepartment[]> => {
        return this.http.get<DtoDepartment[]>(`${this.apiUrlDepartment}`);
    };

    public getDepartmentById = (id: number): Observable<DtoDepartment> => {
        return this.http.get<DtoDepartment>(`${this.apiUrlDepartment}/${id}`);
    };

    public saveDepartment = (dtoObject: DtoDepartment): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(this.apiUrlDepartment, dtoObject);
    };

    public deleteDepartmentById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlDepartment}/${id}`);
    };

    public getDepartmentNextIndex = (): Observable<number> => {
        return this.http.get<number>(`${this.apiUrlDepartment}/nextindex`);
    };

    public checkDepartmentIndexAvailable = (id: number, index: number): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlDepartment}/indexavailable/${id}/${index}`);
    };

    public canDeactivateDepartment = (id: number): Observable<DtoCheckResult> => {
        return this.http.get<DtoCheckResult>(`${this.apiUrlDepartment}/candeactivate/${id}`);
    };

    // SUPPLIER
    public getSuppliers = (): Observable<DtoSupplier[]> => {
        return this.http.get<DtoSupplier[]>(`${this.apiUrlSupplier}`);
    };

    public getSupplierById = (id: number): Observable<DtoSupplier> => {
        return this.http.get<DtoSupplier>(`${this.apiUrlSupplier}/${id}`);
    };

    public saveSupplier = (dtoObject: DtoSupplier): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(this.apiUrlSupplier, dtoObject);
    };

    public deleteSupplierById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlSupplier}/${id}`);
    };

    public getSupplierNextIndex = (): Observable<number> => {
        return this.http.get<number>(`${this.apiUrlSupplier}/nextindex`);
    };

    public checkSupplierIndexAvailable = (id: number, index: number): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlSupplier}/indexavailable/${id}/${index}`);
    };

    // CUSTOMER ORIGIN
    public getCustomerOrigins = (): Observable<DtoCustomerOrigin[]> => {
        return this.http.get<DtoCustomerOrigin[]>(`${this.apiUrlCustomerOrigin}`);
    };

    public getCustomerOriginById = (id: number): Observable<DtoCustomerOrigin> => {
        return this.http.get<DtoCustomerOrigin>(`${this.apiUrlCustomerOrigin}/${id}`);
    };

    public saveCustomerOrigin = (dtoObject: DtoCustomerOrigin): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(this.apiUrlCustomerOrigin, dtoObject);
    };

    public deleteCustomerOriginById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlCustomerOrigin}/${id}`);
    };

    public getCustomerOriginNextIndex = (): Observable<number> => {
        return this.http.get<number>(`${this.apiUrlCustomerOrigin}/nextindex`);
    };

    public checkCustomerOriginIndexAvailable = (id: number, index: number): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlCustomerOrigin}/indexavailable/${id}/${index}`);
    };

    // BTW GROUP
    public getBtwGroups = (): Observable<DtoBtwGroup[]> => {
        return this.http.get<DtoBtwGroup[]>(`${this.apiUrlBtwGroup}`);
    };

    public getBtwGroupById = (id: number): Observable<DtoBtwGroup> => {
        return this.http.get<DtoBtwGroup>(`${this.apiUrlBtwGroup}/${id}`);
    };

    public saveBtwGroup = (dtoObject: DtoBtwGroup): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(this.apiUrlBtwGroup, dtoObject);
    };

    public deleteBtwGroupById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlBtwGroup}/${id}`);
    };

    public checkBtwGroupPercentageAvailable = (id: number, percentage: number): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlBtwGroup}/percentageavailable/${id}/${percentage}/`);
    };

    // MUTATION GROUP
    public getMutationGroups = (): Observable<DtoMutationGroup[]> => {
        return this.http.get<DtoMutationGroup[]>(`${this.apiUrlMutationGroup}`);
    };

    public getMutationGroupById = (id: number): Observable<DtoMutationGroup> => {
        return this.http.get<DtoMutationGroup>(`${this.apiUrlMutationGroup}/${id}`);
    };

    public saveMutationGroup = (dtoObject: DtoMutationGroup): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(this.apiUrlMutationGroup, dtoObject);
    };

    public deleteMutationGroupById = (id: number): Observable<DtoDeleteResult> => {
        return this.http.delete<DtoDeleteResult>(`${this.apiUrlMutationGroup}/${id}`);
    };

    public getMutationGroupNextIndex = (): Observable<number> => {
        return this.http.get<number>(`${this.apiUrlMutationGroup}/nextindex`);
    };

    public checkMutationGroupIndexAvailable = (id: number, index: number): Observable<boolean> => {
        return this.http.get<boolean>(`${this.apiUrlMutationGroup}/indexavailable/${id}/${index}`);
    };

    // FINANCIAL
    public getFinancialSettings = (): Observable<DtoFinancialSettingsUser> => {
        return this.http.get<DtoFinancialSettingsUser>(`${this.apiUrlUser}/general/financial`);
    };

    public saveFinancialSettings = (dtoObject: DtoFinancialSettingsUser): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(`${this.apiUrlUser}/general/financial`, dtoObject);
    };

    // OTHER
    public getOtherSettings = (): Observable<DtoOtherSettingsUserSetting> => {
        return this.http.get<DtoOtherSettingsUserSetting>(`${this.apiUrlUser}/general/other`);
    };

    public saveOtherSettings = (dtoObject: DtoOtherSettingsUserSetting): Observable<DtoSaveResult> => {
        return this.http.post<DtoSaveResult>(`${this.apiUrlUser}/general/other`, dtoObject);
    };

    changeUserPassword(dtoObject: DtoPasswordChange): Observable<DtoSaveResult> {
        return this.http.post<DtoSaveResult>(`user/general/password`, dtoObject);
    };

    checkCurrentUserPassword(password: string): Observable<boolean> {
        return this.http.get<boolean>(`user/general/password/checkcurrent/${password}`);
    };
}


// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Scripts/app/settings/settings-base/settings-base.service.ts